import { useState } from "react";
import { useSession } from "../hooks";
import { navigate } from "../router";
import type { CatchUp as Result } from "../sync/types";
import { IconClose, IconSparkle } from "./icons";
import { t } from "../i18n";

// CatchUp summarizes what the user missed in a channel or thread, citing the
// messages; without a model it lists the highlights.
export function CatchUp({ channel, threadRoot }: { channel: string; threadRoot?: string }) {
  const session = useSession();
  const [result, setResult] = useState<Result | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function run() {
    setBusy(true);
    setError("");
    try {
      setResult(await session.api.catchUp(channel, threadRoot));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  const open = (m: { channel_id: string; id: string }) => navigate(`/c/${m.channel_id}/m/${m.id}`);

  if (!result) {
    return (
      <span className="catch-up">
        <button onClick={run} disabled={busy} title={t("Summarize what you missed")}>
          <IconSparkle size={15} />
          {busy ? t("Summarizing…") : t("Catch up")}
        </button>
        {error && <span className="error"> {error}</span>}
      </span>
    );
  }
  return (
    <div className="catch-up-result" aria-label={t("Catch-up")}>
      <button className="close" onClick={() => setResult(null)} aria-label={t("Close catch-up")}>
        <IconClose size={15} />
      </button>
      {result.reason === "no_model" ? (
        <>
          <p className="note">{t("No model is set up for summaries; here are the highlights of {n} messages.", { n: result.messages })}</p>
          {result.highlights?.length === 0 && <p className="note">{t("Nothing stands out.")}</p>}
          <ul>
            {result.highlights?.map((m) => (
              <li key={m.id}>
                <button onClick={() => open(m)}>
                  {session.name(m.author.id)}: {m.text.slice(0, 160)}
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="summary">
          {result.summary.split(/(\[\d+\])/).map((part, i) => {
            const n = part.match(/^\[(\d+)\]$/)?.[1];
            const cite = n ? result.citations.find((c) => c.n === Number(n)) : undefined;
            return cite ? (
              <button key={i} className="cite" onClick={() => open(cite.message)} title={cite.message.text}>
                [{n}]
              </button>
            ) : (
              <span key={i}>{part}</span>
            );
          })}
        </p>
      )}
    </div>
  );
}
